import {
    Modal, ModalDialog, ModalOverflow, ModalClose,
    Box, Stack, Typography, Button, Chip,
} from "@mui/joy";
import { useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LayoutContext } from "../../context";

import WarningAmberRoundedIcon   from '@mui/icons-material/WarningAmberRounded';
import CalendarMonthRoundedIcon  from '@mui/icons-material/CalendarMonthRounded';
import LocationOnRoundedIcon     from '@mui/icons-material/LocationOnRounded';
import ArrowForwardRoundedIcon   from '@mui/icons-material/ArrowForwardRounded';
import { useNavigate } from "react-router";


function formatDate(dateStr) {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleDateString('ru-RU', {
            day: 'numeric', month: 'long', year: 'numeric',
        });
    } catch { return dateStr; }
}

function daysDiff(dateStr) {
    if (!dateStr) return 0;
    const diff = Math.floor((new Date() - new Date(dateStr)) / 86400000);
    return diff;
}


function OverdueCard({ event, onOpen, onFailNavigate }) {
    const deal    = event?.bitrix_deal;
    const city    = deal?.city?.name || null;
    const address = deal?.address || null;
    const full    = [city, address].filter(Boolean).join(', ') || '—';
    const days    = daysDiff(event?.date_event);

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
        >
            <Box sx={{
                borderRadius: '16px',
                border: '1.5px solid rgba(195,0,19,0.15)',
                background: 'rgba(195,0,19,0.03)',
                p: 2,
                transition: 'all 0.18s',
                '&:hover': { borderColor: 'rgba(195,0,19,0.35)', background: 'rgba(195,0,19,0.06)' },
            }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1.2 }}>
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                        <Typography sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: '0.92rem' }}>
                            Событие #{event?.bitrix_deal?.id}
                        </Typography>
                        <Chip size="sm" sx={{ background: 'rgba(195,0,19,0.1)', color: '#c30013', fontWeight: 700, border: 'none', fontSize: '0.68rem' }}>
                            Просрочено на {days} {days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'}
                        </Chip>
                    </Stack>
                </Stack>

                <Stack spacing={0.7} sx={{ mb: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                        <CalendarMonthRoundedIcon sx={{ fontSize: 15, color: '#c30013', flexShrink: 0 }} />
                        <Typography level="body-xs" sx={{ color: '#c30013', fontWeight: 600 }}>
                            {formatDate(event?.date_event)}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="flex-start" spacing={0.8}>
                        <LocationOnRoundedIcon sx={{ fontSize: 15, color: 'text.tertiary', flexShrink: 0, mt: 0.1 }} />
                        <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1.4 }}>
                            {full}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                    <Button
                        variant="outlined"
                        color="danger"
                        onClick={() => onOpen(event)}
                        sx={{ flex: 1, borderRadius: '10px', fontWeight: 700 }}
                    >
                        Открыть
                    </Button>
                    <Button
                        variant="solid"
                        color="danger"
                        onClick={() => onFailNavigate(event)}
                        sx={{ flex: 1, borderRadius: '10px', fontWeight: 700 }}
                    >
                        Отметить неудачным
                    </Button>
                </Stack>
            </Box>
        </motion.div>
    );
}


export default function ModalOverdueEvents() {
    const {
        modalOverdueEvents, setModalOverdueEvents,
        setModalEvent,
    } = useContext(LayoutContext);
    const navigate = useNavigate();

    const handleClose = () => setModalOverdueEvents({ open: false, events: [] });

    const handleOpenEvent = (event) => {
        handleClose();
        // Небольшая задержка чтобы эта модалка успела закрыться
        setTimeout(() => setModalEvent({ open: true, event }), 120);
    };

    // Неудачное закрытие теперь через отдельную страницу (модалка ломалась)
    const handleFailNavigate = (event) => {
        handleClose();
        navigate(`/surveyor/fail/${event?.bitrix_deal?.id}`);
    };

    const events = modalOverdueEvents?.events || [];

    return (
        <AnimatePresence>
            {modalOverdueEvents?.open && (
                <Modal
                    open={modalOverdueEvents?.open}
                    onClose={handleClose}
                    sx={{ zIndex: 10102 }}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit:    { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3 },
                        },
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            component={motion.div}
                            initial={{ opacity: 0, y: -20, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0,   scale: 1    }}
                            exit={{ opacity: 0, y: 20,  scale: 0.97 }}
                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                            sx={{
                                p: 0, borderRadius: '22px', border: 'none',
                                boxShadow: '0 8px 48px rgba(195,0,19,0.2)',
                                gap: 0,
                                width: { xs: '95vw', sm: 480 },
                                maxWidth: 480,
                            }}
                        >
                            {/* ── Шапка ── */}
                            <Box sx={{
                                background: 'linear-gradient(135deg, #c30013 0%, #e05060 100%)',
                                px: 3, pt: 3, pb: 2.5,
                                borderRadius: '21px 21px 10px 10px',
                            }}>
                                <ModalClose sx={{
                                    position: 'absolute', top: 14, right: 14,
                                    color: 'rgba(255,255,255,0.8)',
                                    '&:hover': { color: 'white', background: 'rgba(255,255,255,0.15)' },
                                }} />

                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box sx={{
                                        width: 44, height: 44, borderRadius: '13px',
                                        background: 'rgba(255,255,255,0.2)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: 'white',
                                    }}>
                                        <WarningAmberRoundedIcon sx={{ fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                        <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Требуют внимания
                                        </Typography>
                                        <Typography level="title-md" sx={{ color: 'white', fontWeight: 800, lineHeight: 1.2 }}>
                                            {events.length} просроченных {events.length === 1 ? 'событие' : events.length < 5 ? 'события' : 'событий'}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '12px', background: 'rgba(255,255,255,0.12)' }}>
                                    <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>
                                        У вас есть события, дата которых уже прошла. Пожалуйста, отметьте каждое из них как завершённое или неудачное.
                                    </Typography>
                                </Box>
                            </Box>

                            {/* ── Список событий ── */}
                            <Box sx={{
                                px: 2.5, pt: 2, pb: 2.5,
                                maxHeight: '60vh', overflowY: 'auto',
                            }}>
                                <Stack spacing={1.5}>
                                    {events.map(event => (
                                        <OverdueCard
                                            key={event.id}
                                            event={event}
                                            onOpen={handleOpenEvent}
                                            onFailNavigate={handleFailNavigate}
                                        />
                                    ))}
                                </Stack>
                            </Box>

                            {/* ── Кнопка закрыть ── */}
                            <Box sx={{
                                px: 2.5, pb: 2.5,
                                borderTop: '1px solid rgba(195,0,19,0.08)',
                                pt: 2,
                            }}>
                                <Button
                                    fullWidth variant="outlined" color="danger"
                                    onClick={handleClose}
                                    sx={{ borderRadius: '12px', fontWeight: 700 }}
                                >
                                    Закрыть и разобраться позже
                                </Button>
                            </Box>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}